import { useEffect, useState } from "react";
import { Music, Play, TrendingUp, Search } from "lucide-react";
import type { AnalyticsOverviewResponse } from "@wizard-sounds/shared";
import { api } from "@/lib/api";
import { formatPlayCount } from "@/lib/utils";

export function AdminDashboard() {
  const [data, setData] = useState<AnalyticsOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.admin
      .analyticsOverview()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-20 text-center text-fog">
        Failed to load analytics
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-display text-2xl font-bold tracking-display">Dashboard</h1>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Play className="h-5 w-5" />}
          label="Total Plays"
          value={formatPlayCount(data.total_plays)}
        />
        <StatCard
          icon={<Music className="h-5 w-5" />}
          label="Sounds"
          value={String(data.total_sounds)}
        />
        <StatCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="Plays Today"
          value={formatPlayCount(data.plays_today)}
        />
        <StatCard
          icon={<Search className="h-5 w-5" />}
          label="Listeners (30d)"
          value={formatPlayCount(data.unique_sessions)}
        />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <h2 className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            Top Sounds
          </h2>
          <div className="mt-3 space-y-2">
            {data.top_sounds.slice(0, 5).map((item, i) => (
              <div
                key={item.sound.id}
                className="flex items-center gap-3 rounded-xl bg-slate-raised px-3 py-2"
              >
                <span className="font-mono text-[11px] text-fog">
                  #{i + 1}
                </span>
                <span className="flex-1 truncate text-sm text-parchment">
                  {item.sound.title}
                </span>
                <span className="font-mono text-[11px] text-fog">
                  {formatPlayCount(item.play_count)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <h2 className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            Top Search Queries
          </h2>
          <div className="mt-3 space-y-2">
            {data.top_search_queries.slice(0, 5).map((item) => (
              <div
                key={item.query}
                className="flex items-center gap-3 rounded-xl bg-slate-raised px-3 py-2"
              >
                <span className="flex-1 truncate text-sm text-parchment">{item.query}</span>
                <span className="font-mono text-[11px] text-fog">
                  {item.count}x
                </span>
              </div>
            ))}
            {data.top_search_queries.length === 0 && (
              <p className="text-sm text-fog">No searches yet</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[18px] border border-mist bg-slate p-4">
      <div className="flex items-center gap-2 text-fog">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <p className="mt-2 font-display text-3xl font-bold tracking-display">{value}</p>
    </div>
  );
}
